import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Typography, 
  Tooltip, 
  tableCellClasses, 
  styled, 
  Button, 
  ButtonProps,
  TablePagination,
  IconButton,
} from '@mui/material';
import AppLayout from '../../layouts/AppLayout';
import { fetchData } from '../../utils/apiClient';
import { PERMISSIONS } from '../../constants/permissions';
import { ArrowBackIosNew } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { purple } from '@mui/material/colors';

// Job type definitions
interface Product {
  productUuid: string;
  title: string;
}

interface Job {
  jobId: string;
  jobType: string;
  status: string;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  product: Product;
}

interface JobPagination {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  total: number;
  hasNext: string;
  nextToken: string;
  lastEvaluatedKey: string;
}

interface JobSearchResponse {
  items: Job[];
  pagination: JobPagination;
}

const STORAGE_KEY = 'jobListRowsPerPage';

const JobListContainer: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? parseInt(saved, 10) : 5;
  });
  const [nextToken, setNextToken] = useState('');
  const [pagination, setPagination] = useState<JobPagination | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  
  // Map to store page number to lastEvaluatedKey mapping (using ref to avoid infinite loops)
  const pageToLastEvaluatedKeyMapRef = useRef<Map<number, string>>(
    new Map([[0, '']]) // Initialize with first page having empty lastEvaluatedKey
  );

  // Common function to search jobs
  const getJobsApi = useCallback(async (): Promise<JobSearchResponse> => {
    try {
      // Get the lastEvaluatedKey for the current page from the map
      const currentPageLastEvaluatedKey = pageToLastEvaluatedKeyMapRef.current.get(page) || '';
      
      const response = await fetchData(
        `/jobs?page_size=${rowsPerPage}&page_number=${page}&last_evaluated_key=${currentPageLastEvaluatedKey}`,
        PERMISSIONS.GET_JOBS,
      );
      return response;
    } catch (error) {
      console.error('Error fetching jobs:', error);
      throw error;
    }
  }, [page, rowsPerPage]);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getJobsApi();
      if (response.items && response.items.length > 0) {
        setJobs(response.items);
        if (response.pagination) {
          setPagination(response.pagination);
          setHasNextPage(!!response.pagination.hasNext);
          setHasPreviousPage(page > 0);
          
          // Store the lastEvaluatedKey for the current page in the map
          pageToLastEvaluatedKeyMapRef.current.set(page, response.pagination.lastEvaluatedKey);
          
          // Store nextToken for next request
          if (response.pagination.nextToken) {
            setNextToken(response.pagination.nextToken);
          } else {
            setNextToken('');
          }
        }
      } else {
        setJobs([]);
        setPagination(null);
        setHasNextPage(false);
        setHasPreviousPage(false);
        setNextToken('');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch jobs');
      console.error('Error fetching jobs:', err);
    } finally {
      setLoading(false);
    }
  }, [getJobsApi, page]);

  // load data on component mount and when pagination parameters change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
        fetchJobs();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [fetchJobs]);

  // Auto-refresh every 30 seconds if any job has PENDING status
  useEffect(() => {
    const hasPendingJobs = jobs.some((job) => job.status === 'PENDING');
    
    if (hasPendingJobs && !loading) {
      const refreshInterval = setInterval(() => {
        fetchJobs();
      }, 30000); // 30 seconds
      
      return () => clearInterval(refreshInterval);
    }
  }, [jobs, loading, fetchJobs]);

  const navigate = useNavigate();
  const navigateToProductsPage = () => {
    navigate('/')
  }
  const navigateToJobDetailsPage = (job_id: string) => {
    navigate(`/jobs/${job_id}`)
  }

  // Pagination handlers for MUI TablePagination
  const handleChangePage = (event: unknown, newPage: number) => {
    // Moving to next page - store nextToken for the new page
    if (newPage > page && hasNextPage && nextToken) {
      pageToLastEvaluatedKeyMapRef.current.set(newPage, nextToken);
      setPage(newPage);
    }
    // Moving to previous page - use stored lastEvaluatedKey from map
    else if (newPage < page && hasPreviousPage) {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    setRowsPerPage(newPageSize);
    localStorage.setItem(STORAGE_KEY, newPageSize.toString());
    setPage(0); 
    setNextToken('');
    pageToLastEvaluatedKeyMapRef.current = new Map([[0, '']]);
  };

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.common.black,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));

  const ColorButton = styled(Button)<ButtonProps>(({ theme, color }) => ({
    color: theme.palette.getContrastText(purple[500]),
    backgroundColor: color,
    '&:hover': {
      backgroundColor: purple[700],
    },
  }));

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'FAILED':
        return 'error';
      case 'COMPLETED':
        return 'success';
      case 'PENDING':
        return 'primary';
      default:
        return 'warning';
    }
  };

  return (
    <AppLayout>
      <Box sx={{ p: 3 }}>
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h4" gutterBottom>
            <IconButton onClick={navigateToProductsPage}>
              <ArrowBackIosNew
                fontSize={'small'}
                style={{ marginBottom: '3px', marginRight: '8px' }}
              />
            </IconButton>
            Jobs Dashboard
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Data Table */}
        <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Job Id</TableCell>
                <TableCell>Product Title</TableCell>
                <TableCell>Product Id</TableCell>
                <TableCell>Job Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Updated At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : jobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      No jobs found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                jobs.map((job, index) => (
                  <TableRow key={index} hover>
                    <TableCell>{job.jobId}</TableCell>
                    <TableCell sx={{ maxWidth: 300, paddingY: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'wrap' }}>
                      {job.product ? job.product.title : 'N/A'}
                    </TableCell>
                    <TableCell>{job.product ? job.product.productUuid : 'N/A'}</TableCell>
                    <TableCell sx={{ py: 6 }}>{job.jobType}</TableCell>
                    <StyledTableCell>
                      <Box>
                        <Tooltip title="View Job Details">
                          <ColorButton
                            variant="contained"
                            color={getStatusColor(job.status)}
                            style={{
                              borderRadius: 50,
                              padding: '4px 8px',
                              fontSize: '0.625rem',
                            }}
                            size={'small'}
                            onClick={() => navigateToJobDetailsPage(job.jobId)}
                          >
                            {job.status}
                          </ColorButton>
                        </Tooltip>
                      </Box>
                    </StyledTableCell>
                    <TableCell sx={{ py: 6 }}>{job.createdBy}</TableCell>
                    <TableCell sx={{ py: 6 }}>
                      {new Date(job.createdAt).toLocaleString('en-US', {
                        month: 'short',
                        day: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true,
                      })}
                    </TableCell>
                    <TableCell sx={{ py: 6 }}>
                      {new Date(job.updatedAt).toLocaleString('en-US', {
                        month: 'short',
                        day: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true,
                      })}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={pagination?.totalItems || -1}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) => {
            if (pagination?.total) {
              return `${from}-${to} of ${pagination?.total ? pagination.total : count}`;
            }
            return `${from}-${to} of more than ${to}`;
          }}
          backIconButtonProps={{
            disabled: !hasPreviousPage || loading,
          }}
          nextIconButtonProps={{
            disabled: !hasNextPage || loading,
          }}
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        />
      </Box>

    </AppLayout>
  );
};

export default JobListContainer;
